import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { useFetchWidgetID } from "@faire/retailer-visitor-shared/hooks/hCaptcha/useFetchWidgetId";
import { hCaptchaQueryKey, logHCaptchaError } from "@faire/retailer-visitor-shared/lib/captchaUtils";
import { getSettingExperimentalHCaptchaTimeoutMS, getSettingAndAssignEnableExperimentalLoginLatencyInterventions, getSettingHCaptchaTimeoutMS } from "@faire/retailer-visitor-shared/settings/getSettingHCaptchaTimeoutMS";
import { getSettingHCaptchaTokenStaleTimeS } from "@faire/retailer-visitor-shared/settings/getSettingHCaptchaTokenStaleTimeS";
import { useHCaptchaStore } from "@faire/retailer-visitor-shared/stores/domain/HCaptchaStore";
import { delay } from "@faire/web--source/common/delay";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { logError } from "@faire/web--source/common/logging";
import { TimingMarker } from "@faire/web--source/common/timings/TimingMarker";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useMutation as usePostHCaptchaMutation } from "@faire/web-api--source/api/v2/users/retailer/hcaptcha/post-hooks";
import { trackSigninHcaptchaApiLatencyTimingPair } from "@faire/web-api--source/events/signin/actionUnknown/hcaptchaApiLatencyTimingPair";
import { trackSigninHcaptchaUserWaitTiming, trackSigninHcaptchaUserWaitTimingTraceDataRequested, trackSigninHcaptchaUserWaitTimingTraceDataRetrieved, trackSigninHcaptchaUserWaitTimingTraceRetreiveFailed } from "@faire/web-api--source/events/signin/timing/hcaptchaUserWait";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ISubmitHCaptchaDataRequest } from "@faire/web-api--source/indigofair/data/ISubmitHCaptchaDataRequest";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
var S_TO_MS = 1000;
var timeBeforeRefetchMS = function() {
    return getSettingHCaptchaTokenStaleTimeS() * S_TO_MS;
};
/**
 * Prefetches hCaptcha token and returns fetchHCaptchaToken. Should be used ahead of calling
 * endpoints we want to protect with rate limiting. Races against timeout (value of
 * getSettingHCaptchaTimeoutMS()) to prevent degrading user experience too much. If timeout wins,
 * return an error.
 *
 * @param enabled whether to initialize hCaptcha (costs quota) and prefetch token;
 * if false, calling fetchHCaptchaToken will return an error
 *
 * returns fetchHCaptchaToken(shouldPostTokenToBackend: boolean)
 * When called, returns prefetched value or kicks off a new fetch. If shouldPostTokenToBackend
 * is true, will also post the token to the backend – this might be desireable if we are not
 * including the token in another backend API request (e.g. the login request accepts an
 * hCaptcha token)
 */ export var useFetchHCaptchaToken = function(enabled) {
    var postHCaptchaToBE = usePostHCaptchaToBE().postHCaptchaToBE;
    var _useFetchTokenWithTimeout = useFetchTokenWithTimeout(enabled), fetchTokenWithTimeout = _useFetchTokenWithTimeout.fetchTokenWithTimeout, prefetchToken = _useFetchTokenWithTimeout.prefetchToken;
    prefetchToken();
    var fetchHCaptchaToken = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(shouldPostTokenToBackend) {
            var data, data1;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!!enabled) return [
                            3,
                            2
                        ];
                        data = ILoginRequest.IHCaptchaData.build({
                            error: ILoginRequest.IHCaptchaData.HCaptchaError.SETTING_OFF
                        });
                        return [
                            4,
                            postHCaptchaToBE(shouldPostTokenToBackend, data)
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2,
                            data
                        ];
                    case 2:
                        trackPreFetchingToken();
                        return [
                            4,
                            fetchTokenWithTimeout()
                        ];
                    case 3:
                        data1 = _state.sent();
                        trackPostFetchingToken(data1);
                        // we must await sending data to BE to avoid race conditions
                        // with the requests made after this point (i.e. login)
                        return [
                            4,
                            postHCaptchaToBE(shouldPostTokenToBackend, data1)
                        ];
                    case 4:
                        _state.sent();
                        return [
                            2,
                            data1
                        ];
                }
            });
        });
        return function(shouldPostTokenToBackend) {
            return _ref.apply(this, arguments);
        };
    }(), [
        enabled,
        fetchTokenWithTimeout,
        postHCaptchaToBE
    ]);
    return {
        fetchHCaptchaToken: fetchHCaptchaToken
    };
};
var trackPreFetchingToken = function() {
    trackSigninHcaptchaUserWaitTiming();
    trackSigninHcaptchaUserWaitTimingTraceDataRequested();
};
var trackPostFetchingToken = function(data) {
    if (data.token) {
        trackSigninHcaptchaUserWaitTimingTraceDataRetrieved();
    } else {
        trackSigninHcaptchaUserWaitTimingTraceRetreiveFailed();
    }
};
var hCaptchaTimeout = /*#__PURE__*/ function() {
    var _ref = _async_to_generator(function() {
        var delayMS;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    delayMS = getSettingAndAssignEnableExperimentalLoginLatencyInterventions() ? getSettingExperimentalHCaptchaTimeoutMS() : getSettingHCaptchaTimeoutMS();
                    return [
                        4,
                        delay(delayMS)
                    ];
                case 1:
                    _state.sent();
                    throw new Error(ILoginRequest.IHCaptchaData.HCaptchaError.TIMED_OUT);
            }
        });
    });
    return function hCaptchaTimeout() {
        return _ref.apply(this, arguments);
    };
}();
var useFetchTokenWithTimeout = function(enabled) {
    var _useFetchWidgetID = useFetchWidgetID(enabled), fetchWidgetID = _useFetchWidgetID.fetchWidgetID, widgetID = _useFetchWidgetID.widgetID;
    var queryClient = useQueryClient();
    var fetchHCaptchaData = useFetchHCaptchaData(enabled).fetchHCaptchaData;
    var prefetchToken = useCallback(function() {
        if (enabled && widgetID) {
            queryClient.prefetchQuery({
                queryKey: hCaptchaQueryKey(widgetID),
                queryFn: function() {
                    return fetchHCaptchaData(widgetID);
                },
                staleTime: timeBeforeRefetchMS()
            });
        }
    }, [
        queryClient,
        widgetID,
        fetchHCaptchaData,
        enabled
    ]);
    var fetchTokenWithTimeout = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        var widgetID, promise, token, error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        3,
                        ,
                        4
                    ]);
                    return [
                        4,
                        Promise.race([
                            fetchWidgetID(),
                            hCaptchaTimeout()
                        ])
                    ];
                case 1:
                    widgetID = _state.sent();
                    promise = queryClient.fetchQuery({
                        queryKey: hCaptchaQueryKey(widgetID),
                        queryFn: function() {
                            return fetchHCaptchaData(widgetID);
                        },
                        staleTime: timeBeforeRefetchMS()
                    });
                    return [
                        4,
                        Promise.race([
                            promise,
                            hCaptchaTimeout()
                        ])
                    ];
                case 2:
                    token = _state.sent();
                    return [
                        2,
                        token
                    ];
                case 3:
                    error = _state.sent();
                    return [
                        2,
                        handleHCaptchaDataError(error)
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    }), [
        fetchWidgetID,
        queryClient,
        fetchHCaptchaData
    ]);
    return {
        fetchTokenWithTimeout: fetchTokenWithTimeout,
        prefetchToken: prefetchToken
    };
};
var useFetchHCaptchaData = function(enabled) {
    var widgetError = useFetchWidgetID(enabled).widgetError;
    var fetchHCaptchaData = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(widgetID) {
            var hcaptcha, fetchedAt, start, res, end;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (widgetError) {
                            return [
                                2,
                                Promise.reject(new Error(widgetError))
                            ];
                        }
                        if (!widgetID) {
                            return [
                                2,
                                Promise.reject(new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_WIDGET))
                            ];
                        }
                        hcaptcha = getGlobalProperty("hcaptcha");
                        if (!(hcaptcha === null || hcaptcha === void 0 ? void 0 : hcaptcha.execute)) {
                            return [
                                2,
                                Promise.reject(new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_SCRIPT))
                            ];
                        }
                        fetchedAt = Date.now();
                        start = new TimingMarker();
                        return [
                            4,
                            hcaptcha.execute(widgetID, {
                                async: true
                            })
                        ];
                    case 1:
                        res = _state.sent();
                        end = new TimingMarker();
                        trackSigninHcaptchaApiLatencyTimingPair({
                            start: start,
                            end: end
                        });
                        if (!(res === null || res === void 0 ? void 0 : res.response)) {
                            return [
                                2,
                                Promise.reject(new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_TOKEN))
                            ];
                        }
                        return [
                            2,
                            ILoginRequest.IHCaptchaData.build({
                                token: res.response,
                                token_fetched_at: fetchedAt
                            })
                        ];
                }
            });
        });
        return function(widgetID) {
            return _ref.apply(this, arguments);
        };
    }(), [
        widgetError
    ]);
    return {
        fetchHCaptchaData: fetchHCaptchaData
    };
};
var usePostHCaptchaToBE = function() {
    var _useHCaptchaStore = _sliced_to_array(useHCaptchaStore([
        "submittedTokens"
    ]), 2), submittedTokens = _useHCaptchaStore[0].submittedTokens, setHCaptchaStore = _useHCaptchaStore[1];
    var _usePostHCaptchaMutation = usePostHCaptchaMutation(), postHCaptcha = _usePostHCaptchaMutation.mutateAsync;
    var postHCaptchaToBE = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(shouldPostTokenToBackend, data) {
            var _data_token, error;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!(shouldPostTokenToBackend && !submittedTokens.includes((_data_token = data.token) !== null && _data_token !== void 0 ? _data_token : ""))) return [
                            3,
                            4
                        ];
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        return [
                            4,
                            postHCaptcha([
                                ISubmitHCaptchaDataRequest.build({
                                    h_captcha_data: data
                                })
                            ])
                        ];
                    case 2:
                        _state.sent();
                        setHCaptchaStore(function(stale) {
                            return _object_spread_props(_object_spread({}, stale), {
                                submittedTokens: _to_consumable_array(stale.submittedTokens).concat([
                                    data.token
                                ]).filter(isNotUndefined)
                            });
                        });
                        return [
                            3,
                            4
                        ];
                    case 3:
                        error = _state.sent();
                        logError(error);
                        return [
                            3,
                            4
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return function(shouldPostTokenToBackend, data) {
            return _ref.apply(this, arguments);
        };
    }(), [
        postHCaptcha,
        setHCaptchaStore,
        submittedTokens
    ]);
    return {
        postHCaptchaToBE: postHCaptchaToBE
    };
};
var handleHCaptchaDataError = function(error) {
    var errorToLog = (typeof error === "undefined" ? "undefined" : _type_of(error)) === "object" ? error.message : error;
    if (!Object.values(ILoginRequest.IHCaptchaData.HCaptchaError).includes(errorToLog)) {
        logHCaptchaError(errorToLog, ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN);
        return ILoginRequest.IHCaptchaData.build({
            error: ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN
        });
    }
    logHCaptchaError(error, errorToLog);
    return ILoginRequest.IHCaptchaData.build({
        error: errorToLog
    });
};
